/* =========================================================================
   NowPaymentsPayout.tsx – NOWPayments crypto withdrawals (payouts)

   Users request a withdrawal (balance is already deducted at request time).
   Here an admin approves it — which needs their 2FA code, because NOWPayments
   makes 2FA mandatory for payouts — or rejects it, which refunds the user.
   ========================================================================= */
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Stack,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  CircularProgress,
  Alert,
} from "@mui/material";
import { ChevronLeft, ChevronRight, RefreshCw, Check, X } from "lucide-react";
import { format } from "date-fns";
import { apiFetch } from "../utils/api";

/* ---------- API types --------------------------------------------------- */
interface PayoutRow {
  id: number;
  userid: string;
  batch_id: string | null;
  withdrawal_id: string | null;
  address: string;
  currency: string;
  pay_currency: string;
  chain: string | null;
  request_amount: number;
  amount: number;
  status: string;
  failure_reason: string | null;
  created_at: string;
  updated_at: string | null;
}

const STATUSES = ["", "pending", "processing", "waiting", "completed", "failed", "rejected"];

const STATUS_COLOR: Record<string, string> = {
  pending: "#F5A623",
  processing: "#2B6EF5",
  waiting: "#2B6EF5",
  sending: "#2B6EF5",
  completed: "#0ECC68",
  failed: "#F5455C",
  rejected: "#8384A5",
};

const fmt = (iso: string) => (iso ? format(new Date(iso), "yyyy-MM-dd HH:mm") : "-");

const cardSx = { bgcolor: "#0E1831", border: "1px solid #1E2D55", borderRadius: 2 };

const inputSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "#0B1427",
    "& fieldset": { borderColor: "#1E2D55" },
    "&:hover fieldset": { borderColor: "#2B6EF5" },
    "&.Mui-focused fieldset": { borderColor: "#2B6EF5" },
  },
  "& input": { color: "#F9F9F9" },
  "& .MuiInputLabel-root": { color: "#8384A5" },
  "& .MuiInputLabel-root.Mui-focused": { color: "#2B6EF5" },
  "& .MuiSelect-select": { color: "#F9F9F9" },
};

const cellSx = { color: "#F9F9F9", borderColor: "#1E2D55", fontSize: "0.8rem" };
const headSx = { color: "#8384A5", borderColor: "#1E2D55", fontSize: "0.75rem", fontWeight: 700 };

/* ----------------------------------------------------------------------- */
export default function NowPaymentsPayout() {
  const [rows, setRows] = useState<PayoutRow[]>([]);
  const [total, setTotal] = useState(0);
  const [load, setLoad] = useState(false);
  const [err, setErr] = useState("");

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState("pending");
  const size = 25;

  // action dialog
  const [action, setAction] = useState<{ kind: "approve" | "reject"; row: PayoutRow } | null>(null);
  const [code, setCode] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [actErr, setActErr] = useState("");

  const fetchRows = useCallback(async () => {
    setLoad(true);
    setErr("");
    try {
      const qs = new URLSearchParams({ page: String(page), limit: String(size) });
      if (status) qs.set("status", status);
      const res = await apiFetch<{ data: PayoutRow[]; total: number }>(
        `/api/nowpayments/payout/list?${qs.toString()}`
      );
      setRows(res.data || []);
      setTotal(res.total || 0);
    } catch (e: any) {
      setErr(e?.message || "Failed to load payouts");
      setRows([]);
    } finally {
      setLoad(false);
    }
  }, [page, status]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const closeDialog = () => {
    setAction(null);
    setCode("");
    setReason("");
    setActErr("");
  };

  const submitAction = async () => {
    if (!action) return;
    setBusy(true);
    setActErr("");
    try {
      if (action.kind === "approve") {
        if (!code.trim()) {
          setActErr("Enter your 2FA code");
          setBusy(false);
          return;
        }
        await apiFetch(`/api/nowpayments/payout/approve`, {
          method: "POST",
          body: JSON.stringify({ id: action.row.id, verification_code: code.trim() }),
        });
      } else {
        await apiFetch(`/api/nowpayments/payout/reject`, {
          method: "POST",
          body: JSON.stringify({ id: action.row.id, reason: reason.trim() || "Rejected by admin" }),
        });
      }
      closeDialog();
      fetchRows();
    } catch (e: any) {
      setActErr(e?.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };

  const pages = Math.max(1, Math.ceil(total / size));

  return (
    <Box sx={{ p: 3 }}>
      {/* Header + filters */}
      <Paper sx={{ ...cardSx, p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }} justifyContent="space-between">
          <Box>
            <Typography sx={{ color: "#F9F9F9", fontSize: "1rem", fontWeight: 700 }}>
              NOWPayments Withdrawals
            </Typography>
            <Typography sx={{ color: "#8384A5", fontSize: "0.75rem" }}>
              Approve with your 2FA code to send the payout, or reject to refund the user
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.5} alignItems="center">
            <TextField
              select
              size="small"
              label="Status"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
              sx={{ ...inputSx, minWidth: 150 }}
            >
              {STATUSES.map((s) => (
                <MenuItem key={s || "all"} value={s}>
                  {s ? s[0].toUpperCase() + s.slice(1) : "All"}
                </MenuItem>
              ))}
            </TextField>

            <Tooltip title="Refresh">
              <Button
                onClick={fetchRows}
                sx={{ minWidth: 40, bgcolor: "#0B1427", border: "1px solid #1E2D55", color: "#8384A5", "&:hover": { color: "#2B6EF5" } }}
              >
                <RefreshCw size={16} />
              </Button>
            </Tooltip>
          </Stack>
        </Stack>
      </Paper>

      {err && (
        <Alert severity="error" sx={{ mb: 2, bgcolor: "rgba(245,69,92,0.1)", color: "#F5455C" }}>
          {err}
        </Alert>
      )}

      {/* Table */}
      <TableContainer component={Paper} sx={cardSx}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headSx}>ID</TableCell>
              <TableCell sx={headSx}>User</TableCell>
              <TableCell sx={headSx}>Coin / Network</TableCell>
              <TableCell sx={headSx}>Address</TableCell>
              <TableCell sx={headSx} align="right">Requested</TableCell>
              <TableCell sx={headSx} align="right">Sent</TableCell>
              <TableCell sx={headSx}>Status</TableCell>
              <TableCell sx={headSx}>Created</TableCell>
              <TableCell sx={headSx} align="center">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {load ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ ...cellSx, textAlign: "center", py: 4 }}>
                  <CircularProgress size={22} sx={{ color: "#2B6EF5" }} />
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} sx={{ ...cellSx, textAlign: "center", py: 4, color: "#8384A5" }}>
                  No withdrawals found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.id} hover sx={{ "&:hover": { bgcolor: "#162140" } }}>
                  <TableCell sx={cellSx}>#{r.id}</TableCell>
                  <TableCell sx={cellSx}>{r.userid}</TableCell>
                  <TableCell sx={cellSx}>
                    <Typography sx={{ fontSize: "0.8rem", color: "#F9F9F9" }}>{r.currency?.toUpperCase()}</Typography>
                    <Typography sx={{ fontSize: "0.7rem", color: "#8384A5" }}>{r.chain || r.pay_currency}</Typography>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, maxWidth: 180 }}>
                    <Tooltip title={r.address}>
                      <Typography sx={{ fontSize: "0.75rem", color: "#8384A5", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.address}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    {Number(r.request_amount)} {r.currency?.toUpperCase()}
                  </TableCell>
                  <TableCell sx={cellSx} align="right">
                    {r.status === "completed" ? (
                      <span style={{ color: "#0ECC68" }}>{Number(r.amount)} {r.currency?.toUpperCase()}</span>
                    ) : (
                      <span style={{ color: "#8384A5" }}>—</span>
                    )}
                  </TableCell>
                  <TableCell sx={cellSx}>
                    <Tooltip title={r.failure_reason || ""}>
                      <Chip
                        label={r.status}
                        size="small"
                        sx={{
                          bgcolor: `${STATUS_COLOR[r.status] || "#8384A5"}22`,
                          color: STATUS_COLOR[r.status] || "#8384A5",
                          fontSize: "0.7rem",
                          textTransform: "capitalize",
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, color: "#8384A5" }}>{fmt(r.created_at)}</TableCell>
                  <TableCell sx={cellSx} align="center">
                    {r.status === "pending" ? (
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Approve & send payout">
                          <Button
                            size="small"
                            onClick={() => setAction({ kind: "approve", row: r })}
                            sx={{ minWidth: 34, bgcolor: "rgba(14,204,104,0.12)", color: "#0ECC68", "&:hover": { bgcolor: "rgba(14,204,104,0.25)" } }}
                          >
                            <Check size={15} />
                          </Button>
                        </Tooltip>
                        <Tooltip title="Reject & refund">
                          <Button
                            size="small"
                            onClick={() => setAction({ kind: "reject", row: r })}
                            sx={{ minWidth: 34, bgcolor: "rgba(245,69,92,0.12)", color: "#F5455C", "&:hover": { bgcolor: "rgba(245,69,92,0.25)" } }}
                          >
                            <X size={15} />
                          </Button>
                        </Tooltip>
                      </Stack>
                    ) : (
                      <Typography sx={{ fontSize: "0.7rem", color: "#8384A5" }}>—</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Typography sx={{ color: "#8384A5", fontSize: "0.75rem" }}>
          {total} total · page {page} / {pages}
        </Typography>
        <Button
          disabled={page <= 1}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          sx={{ minWidth: 36, color: "#8384A5", "&.Mui-disabled": { color: "#3A3F5C" } }}
        >
          <ChevronLeft size={16} />
        </Button>
        <Button
          disabled={page >= pages}
          onClick={() => setPage((p) => p + 1)}
          sx={{ minWidth: 36, color: "#8384A5", "&.Mui-disabled": { color: "#3A3F5C" } }}
        >
          <ChevronRight size={16} />
        </Button>
      </Stack>

      {/* Approve / Reject dialog */}
      <Dialog open={!!action} onClose={busy ? undefined : closeDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: "#0E1831", color: "#F9F9F9", fontSize: "1rem" }}>
          {action?.kind === "approve" ? "Approve & send payout" : "Reject withdrawal"}
          {action && ` · #${action.row.id}`}
        </DialogTitle>
        <DialogContent sx={{ bgcolor: "#0E1831", pt: 2 }}>
          {action && (
            <Box sx={{ mb: 2 }}>
              <Typography sx={{ color: "#8384A5", fontSize: "0.75rem" }}>
                {Number(action.row.request_amount)} {action.row.currency?.toUpperCase()}
                {action.row.chain ? ` · ${action.row.chain}` : ""}
              </Typography>
              <Typography sx={{ color: "#F9F9F9", fontSize: "0.75rem", wordBreak: "break-all", mt: 0.5 }}>
                {action.row.address}
              </Typography>
            </Box>
          )}

          {action?.kind === "approve" ? (
            <>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="2FA code"
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                inputProps={{ maxLength: 6 }}
                sx={inputSx}
              />
              <Typography sx={{ color: "#8384A5", fontSize: "0.7rem", mt: 1 }}>
                Enter the 6-digit code from the NOWPayments Google Authenticator. The code expires every 30s — if it
                fails, the request stays pending and you can retry.
              </Typography>
            </>
          ) : (
            <>
              <TextField
                autoFocus
                fullWidth
                size="small"
                label="Reason (optional)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                sx={inputSx}
              />
              <Typography sx={{ color: "#8384A5", fontSize: "0.7rem", mt: 1 }}>
                The amount will be refunded to the user's balance.
              </Typography>
            </>
          )}

          {actErr && (
            <Alert severity="error" sx={{ mt: 2, bgcolor: "rgba(245,69,92,0.1)", color: "#F5455C", fontSize: "0.75rem" }}>
              {actErr}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: "#0E1831", px: 3, pb: 2 }}>
          <Button onClick={closeDialog} disabled={busy} sx={{ color: "#8384A5" }}>
            Cancel
          </Button>
          <Button
            onClick={submitAction}
            disabled={busy}
            variant="contained"
            sx={{
              bgcolor: action?.kind === "approve" ? "#0ECC68" : "#F5455C",
              "&:hover": { bgcolor: action?.kind === "approve" ? "#0BB35C" : "#D93A4F" },
            }}
          >
            {busy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : action?.kind === "approve" ? "Approve & Send" : "Reject & Refund"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
